import { useParams, Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ErrorMessage } from '@/components/common/ErrorMessage'
import { useAlert, useDeleteAlert, useAlertDevices, useAlertRecipients, useUnassignAlertDevice, useUnassignAlertRecipient } from '@/hooks/useAlerts'
import { ArrowLeft, Edit, Trash2, Wifi, User, X } from 'lucide-react'
import { format } from 'date-fns'
import { ROUTES } from '@/config/routes'
import { useToast } from '@/components/ui/use-toast'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog'

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'critical':
      return 'bg-red-100 text-red-800 border-red-300'
    case 'warning':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300'
    case 'info':
      return 'bg-blue-100 text-blue-800 border-blue-300'
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300'
  }
}

const getOperatorLabel = (operator: string) => {
  const operators: Record<string, string> = {
    '>': 'Greater than',
    '<': 'Less than',
    '>=': 'Greater than or equal to',
    '<=': 'Less than or equal to',
    '==': 'Equal to',
    '!=': 'Not equal to'
  }
  return operators[operator] || operator
}

export const AlertDetailPage = () => {
  const { alertId } = useParams<{ alertId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data: alert, isLoading, error } = useAlert(alertId!)
  const { data: alertDevices } = useAlertDevices(alertId!)
  const { data: alertRecipients } = useAlertRecipients(alertId!)
  const deleteAlert = useDeleteAlert()
  const unassignDevice = useUnassignAlertDevice()
  const unassignRecipient = useUnassignAlertRecipient()

  const handleDelete = async () => {
    try {
      await deleteAlert.mutateAsync(alertId!)
      navigate(ROUTES.ALERTS)
    } catch (err) {
      console.error('Failed to delete alert:', err)
    }
  }

  const handleUnassignDevice = async (deviceId: string) => {
    try {
      await unassignDevice.mutateAsync({ alertId: alertId!, deviceId })
      toast({ title: 'Device removed from alert' })
    } catch {
      toast({ title: 'Failed to remove device', variant: 'destructive' })
    }
  }

  const handleUnassignRecipient = async (userId: string) => {
    try {
      await unassignRecipient.mutateAsync({ alertId: alertId!, userId })
      toast({ title: 'Recipient removed from alert' })
    } catch {
      toast({ title: 'Failed to remove recipient', variant: 'destructive' })
    }
  }

  if (isLoading) return <LoadingSpinner />
  if (error || !alert) return <ErrorMessage message="Failed to load alert details." />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to={ROUTES.ALERTS}>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{alert.alertName}</h1>
            <p className="text-muted-foreground">Alert Configuration</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to={`/alerts/${alertId}/edit`}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Link>
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Alert</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this alert? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Alert Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <Badge variant={alert.isActive ? 'success' : 'secondary'} className="mt-1">
                {alert.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Severity</p>
              <Badge className={`mt-1 ${getSeverityColor(alert.severityLevel)}`}>
                {alert.severityLevel.toUpperCase()}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Device Type</p>
              <p className="mt-1 font-medium">{alert.deviceType || 'All Types'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Measurement</p>
              <p className="mt-1 font-medium">
                {alert.paramLabel || alert.measurementColumn}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Threshold Condition</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">Operator</p>
              <p className="mt-1 font-medium">
                {getOperatorLabel(alert.comparisonOperator)} ({alert.comparisonOperator})
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Threshold Value</p>
              <p className="mt-1 font-medium">
                {alert.thresholdValue} {alert.unit || ''}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Condition</p>
              <div className="mt-1 p-3 bg-muted rounded-md font-mono text-sm">
                {alert.paramLabel || alert.measurementColumn} {alert.comparisonOperator}{' '}
                {alert.thresholdValue} {alert.unit || ''}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Alert Message</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm">{alert.alertMessage}</p>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Recipients ({alertRecipients?.length ?? alert.recipientCount})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-muted-foreground">Notify Linked Users</p>
              <Badge variant={alert.alertLinkedUsers ? 'success' : 'secondary'} className="mt-1">
                {alert.alertLinkedUsers ? 'Yes' : 'No'}
              </Badge>
            </div>
            {alertRecipients && alertRecipients.length > 0 ? (
              <div className="space-y-2">
                {alertRecipients.map((recipient) => (
                  <div key={recipient.id} className="flex items-center justify-between p-2 rounded-md border">
                    <div>
                      <p className="text-sm font-medium">{recipient.firstName} {recipient.lastName}</p>
                      <p className="text-xs text-muted-foreground">{recipient.email}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleUnassignRecipient(recipient.id)}
                      disabled={unassignRecipient.isPending}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No recipients assigned</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wifi className="h-5 w-5" />
              Monitored Devices ({alertDevices?.length ?? alert.deviceCount})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {alertDevices && alertDevices.length > 0 ? (
              <div className="space-y-2">
                {alertDevices.map((device) => (
                  <div key={device.id} className="flex items-center justify-between p-2 rounded-md border">
                    <div>
                      <Link to={`/devices/${device.id}`} className="text-sm font-medium hover:underline">
                        {device.name}
                      </Link>
                      <p className="text-xs text-muted-foreground">{device.serialNumber} &bull; {device.deviceType}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleUnassignDevice(device.id)}
                      disabled={unassignDevice.isPending}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No devices assigned</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Metadata</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground">Alert ID</p>
            <p className="mt-1 font-mono text-xs">{alert.id}</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-sm text-muted-foreground">Created</p>
              <p className="mt-1">{format(new Date(alert.createdAt), 'PPP p')}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Last Updated</p>
              <p className="mt-1">{format(new Date(alert.updatedAt), 'PPP p')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
